import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContextNonNull } from "@contexts/AppContext";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import FeedbackService, { FEEDBACK_SOURCE_FILTER_BAR } from "@services/FeedbackService";
import { toQueryStringParts } from "@utils/apiQueryString";
import { useFilterPresets } from "@hooks/shared/useFilterPresets";
import { useDailyRouteLookups } from "@hooks/shared/useDailyRouteLookups";
import { buildDailyRoutesFilterSections, DAILY_ROUTES_DEFAULT_FILTERS, IDailyRoutesFilterQuery } from "@config/filters/dailyRoutesFilters";
import FilterBar from "../../../shared/FilterBar/FilterBar";
import { useUrlFilters } from "../../../shared/FilterBar/useUrlFilters";

export const DAILY_ROUTES_FILTER_ACTIONS_ID = "daily-routes-filter-actions";

/** The Daily Routes list URL carries only filters. */
const NON_FILTER_PARAMS: string[] = [];

type Props = {
    isSearching: boolean;
    /** Existing Daily Routes fetch: receives the query keyed by IGetDailyRoutesDTO field names */
    searchFn: (query: IDailyRoutesFilterQuery) => void;
};

/**
 * Daily Routes integration of the shared FilterBar (v2 search UI).
 * Screen key is ScreenOptionsEnum.DAILY_ROUTES ("daily_routes"), which is also the presets JSON key.
 * `GET /v1/daily_routes` takes typed query fields, so the query object is handed to the screen as is.
 */
export const DailyRoutesFilterBar = (props: Props) => {
    const { searchFn } = props;
    const { userModel, supplierModel, vendorModel, uiContext } = useAppContextNonNull();
    const navigate = useNavigate();
    const lookups = useDailyRouteLookups();

    const sections = useMemo(() => buildDailyRoutesFilterSections({ supplierModel, lookups }), [supplierModel, lookups]);

    // Deep links (e.g. /routes/daily?startDate=2026-09-01): read on mount and again on Back / Forward.
    const { initialFilters, historyKey, isFirstSearch } = useUrlFilters(sections, NON_FILTER_PARAMS);

    const { presets, onPresetsChange } = useFilterPresets(ScreenOptionsEnum.DAILY_ROUTES);

    const handleSearch = useCallback(
        (query: { [apiKey: string]: unknown }) => {
            const parts = toQueryStringParts(query);

            // Keep the URL in sync so filtered views can be shared / deep-linked.
            // The mount search replaces (no extra history entry); user searches push.
            navigate({ pathname: "/routes/daily", search: parts.length ? `?${parts.join("&")}` : "" }, { replace: isFirstSearch.current });
            isFirstSearch.current = false;

            searchFn(query as IDailyRoutesFilterQuery);
        },
        [navigate, searchFn, isFirstSearch]
    );

    const handleFeedback = useCallback(
        async (message: string) => {
            try {
                await FeedbackService.sendFeedback(message, FEEDBACK_SOURCE_FILTER_BAR, { userModel, supplierModel, vendorModel });
                uiContext.notifyFn(null, { message: "feedback_sent", level: NotificationLevelEnum.success });
            } catch (error) {
                console.error("Failed to send FilterBar feedback", error);
                uiContext.notifyFn(null, { message: "feedback_failed", level: NotificationLevelEnum.error });
                throw error;
            }
        },
        [userModel, supplierModel, vendorModel, uiContext]
    );

    if (presets === null) {
        return null;
    }

    return (
        <FilterBar
            key={historyKey}
            sections={sections}
            presets={presets}
            onSearch={handleSearch}
            onPresetsChange={onPresetsChange}
            screenKey={ScreenOptionsEnum.DAILY_ROUTES}
            mode={"drawer"}
            defaultFilters={DAILY_ROUTES_DEFAULT_FILTERS}
            initialFilters={initialFilters}
            allLabel={"all_daily_routes"}
            actionsContainerId={DAILY_ROUTES_FILTER_ACTIONS_ID}
            isSearching={props.isSearching}
            onSendFeedback={handleFeedback}
        />
    );
};

export default DailyRoutesFilterBar;
